import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarIcon, UserIcon, PinIcon, ShieldIcon, CheckBadgeIcon } from "../site/icons.jsx";
import { fmtLong } from "../lib/dates.js";

const IMAGE_ROOT = "https://images.aloharates.com";

function usd(n, currency = "USD") {
  if (n == null || isNaN(n)) return "—";
  const symbol = currency === "USD" ? "$" : "";
  return `${symbol}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency !== "USD" ? " " + currency : ""}`;
}

// The same fee-merge rule the checkout page uses so the receipt matches
function mergeFees(rows) {
  const buckets = new Map();
  for (const t of rows || []) {
    const label = (t.description || "").trim();
    const lower = label.toLowerCase();
    let group;
    if (/resort/.test(lower))              group = "Resort fee";
    else if (/tourism/.test(lower))        group = "Tourism fee";
    else if (/service/.test(lower))        group = "Service fee";
    else if (/cleaning/.test(lower))       group = "Cleaning fee";
    else if (/tax|vat|gst|hst/.test(lower))group = "Taxes";
    else group = label || "Fees";
    buckets.set(group, (buckets.get(group) || 0) + Number(t.amount || 0));
  }
  return [...buckets.entries()].map(([description, amount]) => ({ description, amount }));
}

export default function BookingPage() {
  const url = useMemo(() => new URLSearchParams(location.search), []);
  const tid = url.get("tid");
  const pid = url.get("pid");
  const preloadedId = url.get("id");   // support direct link with an existing bookingId

  // Two ways onto this page:
  //   confirm  — straight off the payment SDK (?tid & ?pid), books then shows it
  //   manage   — guest arrives from their confirmation email with a booking id
  const isConfirmFlow = Boolean(pid && tid);
  // booking | lookup | loading | loaded | error
  const [status, setStatus] = useState(isConfirmFlow ? "booking" : "lookup");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  // ── Manage-booking state ──
  const [lookupId, setLookupId] = useState(preloadedId || "");
  const [lookupEmail, setLookupEmail] = useState("");
  const [busy, setBusy] = useState("");        // "" | "lookup" | "cancel" | "amend"
  const [notice, setNotice] = useState(null);  // { kind: "ok"|"warn", text }
  const [showCancel, setShowCancel] = useState(false);
  // What the cancel call actually told us, kept for this session. The booking
  // record alone can't be trusted for this: amountRefunded stays 0 until the
  // refund settles, so a full-refund cancellation still reads 0 right after.
  const [cancelOutcome, setCancelOutcome] = useState(null); // { refund, currency }
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendForm, setAmendForm] = useState({ firstName: "", lastName: "", email: "", phone: "", remarks: "" });
  // Room name the guest actually chose at checkout. Sandbox returns the stub
  // "Test rate 1" on /book, so this is what we show instead.
  const checkoutRoomName = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(`alohaBooking:${pid}`) || "null")?.roomName || ""; }
    catch { return ""; }
  }, [pid]);
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;
    (async () => {
      // Manage flow: nothing to do on mount. The guest types their booking id
      // and email into the lookup form (or arrives with ?id= prefilled).
      if (!isConfirmFlow) return;
      if (!pid || !tid) {
        setError("Missing booking session. Try again from the checkout page.");
        setStatus("error"); return;
      }

      // ── Refresh guard ────────────────────────────────────────────────
      // /rates/book is not idempotent, and a page refresh re-runs this
      // effect with the same prebookId — which would create a SECOND
      // confirmed booking and a second charge. A durable marker (not the
      // in-memory `called` ref, which dies on reload) makes that impossible.
      const attemptKey = `alohaBookAttempt:${pid}`;
      let prior = null;
      try { prior = JSON.parse(localStorage.getItem(attemptKey) || "null"); } catch {}
      if (prior?.status === "done" && prior.booking) {
        // Already booked on this device — re-render the confirmation instead
        // of booking again.
        setBooking(prior.booking);
        setStatus("loaded");
        return;
      }
      if (prior?.status === "in-flight") {
        // A previous attempt started and we never recorded the outcome. It may
        // well have succeeded upstream, so do NOT resend.
        setError("This booking was already submitted from this device and we didn't get a confirmation back.");
        setStatus("error"); return;
      }
      try { localStorage.setItem(attemptKey, JSON.stringify({ status: "in-flight", startedAt: Date.now() })); } catch {}
      // Lead-guest contact was stashed by checkout so we can rebuild the
      // /book payload after the SDK redirect. Traveler counts are NOT in the
      // stash — they ride on the URL (?adults & ?children) so the payload
      // matches whatever the prebook was priced for.
      let stash = null;
      try { stash = JSON.parse(localStorage.getItem(`alohaBooking:${pid}`) || "null"); } catch {}
      if (!stash) {
        setError("Guest details weren't saved to this device. Please re-do checkout from the property page.");
        setStatus("error"); return;
      }
      const holder = stash.holder || {};
      // Who is actually staying. Same person as the holder for a normal
      // booking; different when checkout was filled in on someone else's
      // behalf, in which case the holder is whoever paid.
      const guest = (stash.guest && stash.guest.firstName) ? stash.guest : holder;
      // LiteAPI wants ONE guest entry per OCCUPANCY (i.e. per room) — the lead
      // guest for that room — NOT one entry per traveler. Verified directly
      // against /rates/book on a live prebook of 1 room for 2 adults:
      //   2 guests occupancyNumber 1,1 -> 4002 invalid occupancy number
      //   2 guests occupancyNumber 1,2 -> 4002 invalid occupancy number
      //   1 guest  occupancyNumber 1   -> passes occupancy validation
      // occupancyNumber is 1-based; 0 is rejected outright. Head counts live
      // in the rate (adultCount/childrenAges) from the prebook, so they must
      // NOT be echoed back as extra guest objects.
      const occCount = Math.max(1, Number(url.get("occ")) || 1);
      const guests = Array.from({ length: occCount }, (_, i) => ({
        occupancyNumber: i + 1,
        firstName: guest.firstName,
        lastName: guest.lastName,
        email: guest.email || holder.email,
        ...(i === 0 ? { phone: guest.phone || holder.phone || "", remarks: stash.remarks || "" } : {})
      }));

      const body = {
        prebookId: pid,
        holder: { firstName: holder.firstName, lastName: holder.lastName, email: holder.email },
        payment: { method: "TRANSACTION_ID", transactionId: tid },
        guests
      };
      // ─────────────────────────────────────────────────────────────────
      // NEVER RETRY THIS CALL. /rates/book is NOT idempotent: calling it
      // three times with one prebookId produced three separate CONFIRMED
      // bookings (WmYxaaG9U / gnGsm17nW / nEO-E4ljT, $487.35 each) against
      // the sandbox. The card is also already charged by the time we get
      // here, so a retry risks double-booking and double-charging.
      //
      // A lost response is indistinguishable from a failed one in the
      // browser, and the lost-response case is exactly when the booking DID
      // commit. So on any failure we stop, keep the identifiers on screen,
      // and tell the guest not to try again.
      // ─────────────────────────────────────────────────────────────────
      try {
        const r = await fetch("/api/book", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body)
        });
        const payload = await r.json();
        console.log("[Booking] /api/book response", payload);
        const err = payload?.error;
        if (r.ok && !err) {
          const data = payload?.data || payload;
          if (!data?.bookingId) throw new Error("Booking confirmation missing bookingId");
          // Record the outcome so a refresh re-renders this confirmation
          // rather than booking a second time.
          try { localStorage.setItem(attemptKey, JSON.stringify({ status: "done", booking: data, at: Date.now() })); } catch {}
          setBooking(data);
          setStatus("loaded");
          return;
        }
        // LiteAPI rejected it outright, so nothing was created — clear the
        // marker so the guest can legitimately try again from checkout.
        try { localStorage.removeItem(attemptKey); } catch {}
        setError(err?.description || err?.message || `Book failed (${r.status})`);
      } catch (netErr) {
        // Network-level failure: the booking may or may not have been created
        // upstream. Do not resend — surface it for manual reconciliation.
        setError(netErr.message || "Network error while confirming your booking");
      }
      setStatus("error");
    })();
  }, [pid, tid, preloadedId]);

  // Derived values from the /book response.
  //
  // LiteAPI /book response shape (differs from /prebook):
  //   bookedRooms[0]        — one entry per room booked
  //     .adults, .children, .childrenAges
  //     .firstName, .lastName, .remarks (per-room)
  //     .roomType.name, .boardName
  //     .rate.retailRate.total       — SINGLE { amount, currency } object
  //     .rate.retailRate.taxesAndFees
  //     .rate.cancellationPolicies
  //     .guests[]           — every traveler on this room
  //   holder                — lead guest at the booking level
  //   hotel.hotelId, .name
  //   checkin, checkout, price, commission, currency
  //   bookingId, hotelConfirmationCode, supplierBookingId, status
  const room = booking?.bookedRooms?.[0] || null;
  const rate = room?.rate || null;
  const holder = booking?.holder || {};
  const hotelName = booking?.hotel?.name || "Your stay";
  const hotelId  = booking?.hotel?.hotelId || booking?.hotel?.id;
  const heroPhoto = hotelId ? `${IMAGE_ROOT}/properties/${hotelId}/0.webp` : null;

  const ci = booking?.checkin;
  const co = booking?.checkout;
  const currency = booking?.currency || rate?.retailRate?.total?.currency || "USD";

  const payToday = Number(booking?.price ?? rate?.retailRate?.total?.amount ?? 0);
  const taxes = rate?.retailRate?.taxesAndFees || [];
  const taxesIncluded = mergeFees(taxes.filter(t => t.included));
  const taxesExcluded = mergeFees(taxes.filter(t => !t.included));
  const includedTotal = taxesIncluded.reduce((s, t) => s + Number(t.amount || 0), 0);
  const payAtProperty = taxesExcluded.reduce((s, t) => s + Number(t.amount || 0), 0);
  const roomSubtotal = Math.max(0, payToday - includedTotal);
  const tripTotal = Math.round((payToday + payAtProperty) * 100) / 100;

  // ── Manage-booking actions ────────────────────────────────────────────
  //
  // NOTE ON ACCESS CONTROL: the worker exposes GET/PUT /api/bookings/:id to
  // any allowed Origin, so the email check below is friction for honest users,
  // NOT real authorization — anyone holding a booking id could call the API
  // directly and cancel a trip. Enforcing the holder email server-side in the
  // worker is the real fix and should land before this page is promoted.
  const emailsMatch = (a, b) =>
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

  // These endpoints don't always answer with JSON — an invalid amend payload
  // returns HTTP 500 with a zero-length body — so never call r.json() directly.
  const readJson = async (r) => {
    const raw = await r.text();
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  const doLookup = async (e) => {
    e?.preventDefault();
    const id = lookupId.trim();
    const email = lookupEmail.trim();
    if (!id || !email) return;
    setBusy("lookup"); setError(null); setNotice(null); setStatus("loading");
    try {
      const r = await fetch(`/api/bookings/${encodeURIComponent(id)}`);
      const payload = await readJson(r);
      const err = payload?.error;
      if (!r.ok || err || !payload) {
        // Same message whether the id is wrong or the email is — don't confirm
        // that a given booking id exists to someone who can't name the holder.
        setError("We couldn't find a booking with that reference and email.");
        setStatus("error"); return;
      }
      const data = payload?.data || payload;
      const holderEmail = data?.holder?.email || data?.email;
      if (!emailsMatch(holderEmail, email)) {
        setError("We couldn't find a booking with that reference and email.");
        setStatus("error"); return;
      }
      setBooking(data);
      setAmendForm({
        firstName: data?.holder?.firstName || "",
        lastName:  data?.holder?.lastName  || "",
        email:     data?.holder?.email     || "",
        phone:     data?.holder?.phone     || "",
        remarks:   ""
      });
      setStatus("loaded");
    } catch (netErr) {
      setError(netErr.message || "Network error looking up your booking.");
      setStatus("error");
    } finally { setBusy(""); }
  };

  const refresh = async (id) => {
    try {
      const r = await fetch(`/api/bookings/${encodeURIComponent(id)}`);
      const payload = await readJson(r);
      if (r.ok && payload && !payload.error) { setBooking(payload.data || payload); return true; }
    } catch {}
    return false;
  };

  const doCancel = async () => {
    const id = booking?.bookingId; if (!id) return;
    setBusy("cancel"); setNotice(null);
    try {
      const r = await fetch(`/api/bookings/${encodeURIComponent(id)}`, { method: "PUT" });
      const payload = await readJson(r);
      const err = payload?.error;
      if (!r.ok || err || !payload) {
        setNotice({ kind: "warn", text: err?.description || err?.message || `Cancellation failed (${r.status})` });
      } else {
        const d = payload?.data || payload;
        // Lead with the refund, not the charge. "A fee of $487.35 applies and
        // $0.00 will be refunded" reads like the guest lost the money AND got
        // fined on top; what they actually want to know is what comes back.
        const refund = Number(d?.refund_amount || 0);
        const cur = d?.currency || currency;
        setCancelOutcome({ refund, currency: cur });
        const reRead = await refresh(id);
        // On a successful re-read the header already states the outcome and the
        // refund, so a notice repeating it verbatim is just noise. Only fall
        // back to the banner when the re-read didn't land.
        setNotice(reRead ? null : {
          kind: "ok",
          text: refund > 0
            ? `Your booking is cancelled. ${usd(refund, cur)} will be returned to your original payment method, usually within a few business days.`
            : String(d?.status || "").toUpperCase() === "CANCELLED"
              ? "Your booking is cancelled. You won't be charged for this stay."
              : "Your booking is cancelled. This rate didn't include a refund, so nothing will be returned."
        });
      }
    } catch (netErr) {
      setNotice({ kind: "warn", text: netErr.message || "Network error while cancelling." });
    } finally { setBusy(""); setShowCancel(false); }
  };

  const doAmend = async (e) => {
    e?.preventDefault();
    const id = booking?.bookingId; if (!id) return;

    const firstName = amendForm.firstName.trim();
    const lastName  = amendForm.lastName.trim();
    const email     = amendForm.email.trim();
    // GUARD: LiteAPI accepts an amend with a missing holder.email, answers 200,
    // and BLANKS the stored email. That would also lock the guest out of this
    // page, since lookup matches on it. Verified against the sandbox. So never
    // send a partial holder — bail before the request.
    if (!firstName || !lastName || !email) {
      setNotice({ kind: "warn", text: "First name, last name and email are all required — leaving one blank would erase it from your booking." });
      return;
    }

    setBusy("amend"); setNotice(null);
    try {
      const body = { holder: { firstName, lastName, email, phone: amendForm.phone.trim() } };
      if (amendForm.remarks.trim()) body.remarks = amendForm.remarks.trim();
      const r = await fetch(`/api/bookings/${encodeURIComponent(id)}/amend`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      // An invalid payload comes back as HTTP 500 with a zero-length body, so
      // r.json() would throw. Read as text and parse defensively.
      const payload = await readJson(r);
      const err = payload?.error;
      if (!r.ok || err) {
        setNotice({
          kind: "warn",
          text: err?.description || err?.message
            || (typeof err === "string" ? err : null)
            || `We couldn't apply that change (error ${r.status}). Your booking is unchanged.`
        });
      } else {
        // The amendment record reports PENDING, but the booking itself updates
        // immediately, so re-read and let the page show the real current values
        // rather than claiming nothing has happened yet.
        setAmendOpen(false);
        await refresh(id);
        setNotice({ kind: "ok", text: "Your guest details were updated. The property may still need to approve the change." });
      }
    } catch (netErr) {
      setNotice({ kind: "warn", text: netErr.message || "Network error while requesting the change." });
    } finally { setBusy(""); }
  };

  // Traveler counts come from the RESPONSE ROOT, not bookedRooms[0].
  // Sandbox stubs bookedRooms out entirely — it returns adults:1, children:0,
  // childrenAges:null and roomType.name "Test rate 1" no matter what was
  // actually booked. The root fields are correct in every case tested
  // (1 adult / 2 adults / 2 adults + 2 kids). Root `children` is a comma
  // string of AGES ("8,10"), not a count; `childrenCount` is the count.
  const urlAdults = Number(url.get("adults")) || 0;
  const urlChildAges = (url.get("children") || "").split(",").filter(Boolean).map(Number);
  const parseAges = v => Array.isArray(v)
    ? v.map(Number).filter(n => !isNaN(n))
    : String(v || "").split(",").filter(Boolean).map(Number).filter(n => !isNaN(n));

  const adults = booking?.adults ?? (room?.adults > 1 ? room.adults : null) ?? urlAdults ?? 1;
  const rootAges = parseAges(booking?.children);
  const childrenAges = rootAges.length ? rootAges
    : (room?.childrenAges?.length ? parseAges(room.childrenAges) : urlChildAges);
  const childrenCount = booking?.childrenCount ?? childrenAges.length;
  // Same story for the room name: prefer a real one, fall back to what the
  // guest actually picked at checkout rather than showing "Test rate 1".
  // Sandbox names every booked room "Test rate N". Prefer a real name, then
  // the one stashed at checkout (same-device only), and otherwise show nothing
  // rather than surfacing the placeholder to a guest.
  const rawRoomName = room?.roomType?.name || "";
  const stubRoomName = /^test rate/i.test(rawRoomName);
  const roomName = (!stubRoomName && rawRoomName) || checkoutRoomName || "";
  const boardName = room?.boardName || room?.board;
  const cancellation = rate?.cancellationPolicies || booking?.cancellationPolicies;
  const guestRemarks = room?.remarks || "";

  const printReceipt = () => window.print();

  const bookingStatus = String(booking?.status || "").toUpperCase();
  const isCancelled = bookingStatus.startsWith("CANCELLED");

  // What to tell a guest about money after cancelling.
  //
  // Do NOT read amountRefunded to decide this. It stays 0 until a refund
  // settles, and when the card was only authorised (paymentStatus
  // "requires_capture") it stays 0 forever because the hold is released rather
  // than refunded. Keying off it told a fully-refunded guest they got nothing.
  //
  // `status` is the reliable signal: CANCELLED means no charge stands,
  // CANCELLED_WITH_CHARGES means some or all of it does. The cancel response
  // is better still when we have it, so prefer that.
  const settledRefund = Number(booking?.amountRefunded || 0);
  const refundLine = (() => {
    const amt = cancelOutcome ? cancelOutcome.refund : settledRefund;
    const cur = cancelOutcome?.currency || currency;
    if (amt > 0) return <>{usd(amt, cur)} is on its way back to your original payment method.</>;
    if (bookingStatus === "CANCELLED") return <>You won't be charged for this stay. Any hold on your card is released within a few business days.</>;
    if (settledRefund > 0) return <>{usd(settledRefund, currency)} is on its way back to your original payment method.</>;
    return <>This rate didn't include a refund, so nothing was returned.</>;
  })();
  // Free-cancellation deadline, when the supplier gave us one.
  // In LiteAPI's cancelPolicyInfos, `cancelTime` is the moment a charge STARTS
  // and `amount` is what you'd be charged from then on. So free cancellation
  // runs UNTIL the earliest cancelTime carrying a non-zero amount — not until
  // a zero-amount entry, which is the free tier's own start.
  const chargeStartsAt = (cancellation?.cancelPolicyInfos || [])
    .filter(p => Number(p.amount) > 0 && p.cancelTime)
    .map(p => p.cancelTime)
    .sort()[0] || null;
  const freeUntil = booking?.lastFreeCancellationDate || chargeStartsAt || null;
  const nonRefundable = cancellation?.refundableTag === "NRFN";
  // Is the free-cancellation window still open right now? Drives whether we
  // promise the full amount back or hedge.
  const freeCancelStillOpen = Boolean(freeUntil) && !nonRefundable && (() => {
    const t = Date.parse(freeUntil);
    return !isNaN(t) && t > Date.now();
  })();

  if (status === "booking") return (
    <div className="min-h-[calc(100dvh-80px)] grid place-items-center bg-sand-50">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full border-4 border-ocean-200 border-t-ocean-600 animate-spin mb-4"/>
        <h1 className="font-display text-2xl text-ink">Confirming your booking…</h1>
        <p className="prose-body text-sm mt-1">Don't close this window. This takes a few seconds.</p>
      </div>
    </div>
  );

  if (status === "loading") return (
    <div className="min-h-[calc(100dvh-80px)] grid place-items-center bg-sand-50">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full border-4 border-ocean-200 border-t-ocean-600 animate-spin mb-4"/>
        <h1 className="font-display text-2xl text-ink">Finding your booking…</h1>
      </div>
    </div>
  );

  // ── Manage: look up a booking from the confirmation email ──
  if (status === "lookup") return (
    <div className="min-h-[calc(100dvh-80px)] grid place-items-center bg-sand-50 px-4 py-10">
      <div className="card p-7 md:p-8 w-full max-w-md">
        <span className="eyebrow">Manage your trip</span>
        <h1 className="font-display text-2xl md:text-3xl text-ink mt-2">Find your booking</h1>
        <p className="prose-body text-sm mt-2">
          Enter the booking reference from your confirmation email, along with the email address it was sent to.
        </p>
        <form onSubmit={doLookup} className="mt-5 space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-soft">Booking reference</span>
            <input value={lookupId} onChange={e => setLookupId(e.target.value)} required autoFocus
              placeholder="e.g. V8mglw-im" autoComplete="off" spellCheck={false}
              className="pill-input px-3 py-2 text-sm font-mono"/>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-soft">Email on the booking</span>
            <input value={lookupEmail} onChange={e => setLookupEmail(e.target.value)} required
              type="email" placeholder="you@example.com" autoComplete="email"
              className="pill-input px-3 py-2 text-sm"/>
          </label>
          <button className="btn-primary w-full py-2.5 text-sm" disabled={busy === "lookup"}>
            {busy === "lookup" ? "Looking…" : "Find my booking"}
          </button>
        </form>
        <p className="text-xs text-ink-muted mt-4">
          Can't find your reference? <a href="/contact/" className="underline">Contact support</a> and we'll look it up for you.
        </p>
      </div>
    </div>
  );

  if (status === "error") return (
    <div className="min-h-[calc(100dvh-80px)] grid place-items-center bg-sand-50 px-4">
      <div className="card p-8 max-w-lg text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-sunset-100 text-sunset-600 grid place-items-center mb-4"><ShieldIcon size={24}/></div>
        <h1 className="font-display text-2xl text-ink">
          {isConfirmFlow ? "Booking couldn't be confirmed" : "We couldn't find that booking"}
        </h1>
        <p className="prose-body text-sm mt-2">{error}</p>
        {!isConfirmFlow && (
          <>
            <p className="text-xs text-ink-muted mt-3">
              Check the reference and email against your confirmation email — both have to match.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button onClick={() => { setError(null); setStatus("lookup"); }} className="btn-primary py-2 px-5 text-sm">Try again</button>
              <a href="/contact/" className="text-sm text-ocean-700 hover:text-ocean-800 underline">Contact support</a>
            </div>
          </>
        )}
        {isConfirmFlow && <>
        <p className="prose-body text-sm mt-3 font-medium text-ink">
          Please don't book again or refresh this page — that can create a second reservation and a second charge.
        </p>
        <p className="text-xs text-ink-muted mt-2">
          Contact <a href="/contact/" className="underline">support</a> with the references below and we'll confirm
          whether your reservation went through and reverse any charge that shouldn't stand.
        </p>
        {(pid || tid) && (
          <dl className="mt-4 text-left inline-block rounded-xl bg-white/70 border border-ink/10 px-4 py-3 text-xs">
            {pid && <div className="flex gap-2"><dt className="text-ink-muted">Booking reference</dt><dd className="font-mono text-ink">{pid}</dd></div>}
            {tid && <div className="flex gap-2 mt-1"><dt className="text-ink-muted">Payment reference</dt><dd className="font-mono text-ink">{tid}</dd></div>}
          </dl>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          <a href="/contact/" className="btn-primary py-2 px-5 text-sm">Contact support</a>
          <a href="/" className="text-sm text-ocean-700 hover:text-ocean-800 underline">Back to home</a>
        </div>
        </>}
      </div>
    </div>
  );

  return (
    <div className="bg-sand-50 min-h-[calc(100dvh-80px)] print:bg-white">
      <div className="container-page py-6 md:py-10 print:py-4 print:max-w-3xl">
        {/* Confirmation banner */}
        <div className="card p-5 md:p-6 flex items-start gap-4 mb-5 print:shadow-none print:border print:border-ink/10">
          <div className={`h-12 w-12 rounded-full grid place-items-center shrink-0 ${isCancelled ? "bg-sunset-100 text-sunset-600" : "bg-ocean-50 text-ocean-700"}`}>
            {isCancelled ? <ShieldIcon size={24}/> : <CheckBadgeIcon size={26}/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${isCancelled ? "text-sunset-600" : "text-ocean-700"}`}>
              {/* LiteAPI's CANCELLED_WITH_CHARGES is an internal distinction.
                  The guest just needs "Cancelled"; the refund line below says
                  what they actually get back. */}
              {isCancelled ? "Cancelled" : "Confirmed"}
            </div>
            <h1 className="font-display text-2xl md:text-3xl text-ink leading-tight">
              {isCancelled ? "This booking is cancelled." : "You're going to Hawaiʻi."}
            </h1>
            <p className="prose-body text-sm mt-1">
              {isCancelled
                ? <>
                    Cancelled{booking?.cancelledAt ? ` on ${fmtLong(String(booking.cancelledAt).slice(0, 10))}` : ""}.{" "}
                    {refundLine}{" "}
                    Keep this page for your records.
                  </>
                : <>We sent a confirmation to <b className="text-ink">{holder.email}</b>. Save this page or download it as PDF for your records.</>}
            </p>
          </div>
          <button onClick={printReceipt} className="btn-secondary shrink-0 text-sm py-2 px-4 print:hidden">Download PDF</button>
        </div>

        {notice && (
          <div className={`card p-4 mb-5 border-l-4 print:hidden ${notice.kind === "ok" ? "border-ocean-500" : "border-sunset-500"}`}>
            <p className="prose-body text-sm">{notice.text}</p>
          </div>
        )}

        {/* ────────── Manage this booking ────────── */}
        {!isConfirmFlow && !isCancelled && (
          <div className="card p-5 md:p-6 mb-5 print:hidden">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg text-ink">Manage this booking</h2>
                <p className="prose-body text-sm mt-1">
                  You can update the lead guest's contact details, or cancel the stay.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { setAmendOpen(v => !v); setShowCancel(false); }}
                  className="btn-secondary text-sm py-2 px-4">
                  {amendOpen ? "Close" : "Edit guest details"}
                </button>
                <button onClick={() => { setShowCancel(v => !v); setAmendOpen(false); }}
                  className="text-sm py-2 px-4 rounded-pill border border-sunset-300 text-sunset-700 hover:bg-sunset-50">
                  Cancel booking
                </button>
              </div>
            </div>

            {amendOpen && (
              <form onSubmit={doAmend} className="mt-5 pt-5 border-t border-ink/5">
                <p className="text-xs text-ink-muted mb-3">
                  Only the lead guest's name and contact details can be changed. Dates, room and traveller
                  numbers can't be edited here — cancel and rebook, or <a href="/contact/" className="underline">contact support</a>.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-soft">First name</span>
                    <input required value={amendForm.firstName} onChange={e => setAmendForm(f => ({ ...f, firstName: e.target.value }))} className="pill-input px-3 py-2 text-sm"/>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-soft">Last name</span>
                    <input required value={amendForm.lastName} onChange={e => setAmendForm(f => ({ ...f, lastName: e.target.value }))} className="pill-input px-3 py-2 text-sm"/>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-soft">Email</span>
                    <input required type="email" value={amendForm.email} onChange={e => setAmendForm(f => ({ ...f, email: e.target.value }))} className="pill-input px-3 py-2 text-sm"/>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-soft">Phone</span>
                    <input value={amendForm.phone} onChange={e => setAmendForm(f => ({ ...f, phone: e.target.value }))} className="pill-input px-3 py-2 text-sm"/>
                  </label>
                  <label className="flex flex-col gap-1 sm:col-span-2">
                    <span className="text-xs font-medium text-ink-soft">Request for the property <span className="text-ink-muted">(optional, not guaranteed)</span></span>
                    <input value={amendForm.remarks} onChange={e => setAmendForm(f => ({ ...f, remarks: e.target.value }))} placeholder="e.g. late arrival around 10pm" className="pill-input px-3 py-2 text-sm"/>
                  </label>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button className="btn-primary py-2 px-5 text-sm" disabled={busy === "amend"}>
                    {busy === "amend" ? "Sending…" : "Save changes"}
                  </button>
                  <button type="button" onClick={() => setAmendOpen(false)} className="text-sm text-ink-muted hover:text-ink underline">Discard</button>
                </div>
              </form>
            )}

            {showCancel && (
              <div className="mt-5 pt-5 border-t border-ink/5">
                <h3 className="text-sm font-semibold text-ink">Cancel this booking?</h3>
                <p className="prose-body text-sm mt-1">
                  {nonRefundable
                    ? <>This rate didn't include a refund, so cancelling won't return the {usd(payToday, currency)} you paid. Your room will be released.</>
                    : freeCancelStillOpen
                      ? <>You'll get your full {usd(payToday, currency)} back, as long as you cancel before <b className="text-ink">{fmtLong(String(freeUntil).slice(0, 10))}</b>.</>
                      : freeUntil
                        ? <>The free-cancellation window closed on <b className="text-ink">{fmtLong(String(freeUntil).slice(0, 10))}</b>, so you may get back less than the {usd(payToday, currency)} you paid. We'll show the exact amount before anything is finalised.</>
                        : <>How much comes back depends on the property's policy. We'll show you the exact refund as soon as you confirm.</>}
                </p>
                <p className="text-xs text-ink-muted mt-2">This can't be undone, and the rate may not be available if you rebook later.</p>
                <div className="mt-4 flex items-center gap-3">
                  <button onClick={doCancel} disabled={busy === "cancel"}
                    className="text-sm py-2 px-5 rounded-pill bg-sunset-600 text-white hover:bg-sunset-700 disabled:opacity-60">
                    {busy === "cancel" ? "Cancelling…" : "Yes, cancel booking"}
                  </button>
                  <button onClick={() => setShowCancel(false)} className="text-sm text-ink-muted hover:text-ink underline">Keep my booking</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start print:grid-cols-1">
          {/* ────────── Left: Confirmation numbers + guest + policy ────────── */}
          <div className="space-y-5">
            {/* Confirmation numbers */}
            <div className="card p-5 md:p-6">
              <h2 className="font-display text-lg text-ink mb-3">Confirmation numbers</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Aloha Rates ID</div>
                  <div className="font-mono text-ink mt-1 break-all">{booking.bookingId}</div>
                </div>
                {booking.hotelConfirmationCode && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Hotel confirmation</div>
                    <div className="font-mono text-ink mt-1 break-all">{booking.hotelConfirmationCode}</div>
                  </div>
                )}
                {booking.supplierBookingId && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Supplier reference</div>
                    <div className="font-mono text-ink mt-1 break-all">{booking.supplierBookingId}</div>
                  </div>
                )}
              </div>
              {/* A cancelled booking is a settled outcome, not a stuck one —
                  the "waiting for confirmation" nudge only applies to a
                  booking that is still trying to become CONFIRMED. */}
              {booking.status && booking.status.toLowerCase() !== "confirmed" && !isCancelled && (
                <div className="mt-3 text-xs text-sunset-600">Status: {booking.status}. If this doesn't change to confirmed within 15 minutes, contact support.</div>
              )}
            </div>

            {/* Guest details */}
            <div className="card p-5 md:p-6">
              <h2 className="font-display text-lg text-ink mb-3">Guest details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Lead guest</div>
                  <div className="text-ink mt-1">{holder.firstName} {holder.lastName}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Email</div>
                  <div className="text-ink mt-1 break-all">{holder.email}</div>
                </div>
                {holder.phone && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Phone</div>
                    <div className="text-ink mt-1">{holder.phone}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Travelers</div>
                  <div className="text-ink mt-1">
                    {adults} adult{adults !== 1 ? "s" : ""}
                    {childrenCount > 0 && <> · {childrenCount} child{childrenCount !== 1 ? "ren" : ""}{childrenAges.length ? ` (ages ${childrenAges.join(", ")})` : ""}</>}
                  </div>
                </div>
              </div>
              {guestRemarks && (
                <div className="mt-4 border-t border-ink/5 pt-3">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Special requests</div>
                  <div className="text-sm text-ink-soft mt-1 italic">"{guestRemarks}"</div>
                  <div className="text-[11px] text-ink-muted mt-1">Not guaranteed by the property.</div>
                </div>
              )}
            </div>

            {/* Cancellation policy */}
            {cancellation && (
              <div className="card p-5 md:p-6">
                <h2 className="font-display text-lg text-ink mb-3">Cancellation policy</h2>
                {cancellation.refundableTag === "RFN" ? (
                  // Uses the SAME freeUntil the cancel dialog uses, so the card
                  // and the dialog can never quote different deadlines.
                  <div className="text-sm text-ink-soft">
                    {freeUntil
                      ? <>Cancel before <b className="text-ink">{fmtLong(String(freeUntil).slice(0, 10))}</b> and you'll get your full {usd(payToday, currency)} back. After that date the booking no longer qualifies for a refund.</>
                      : "You can cancel this booking and get your money back — see property terms for details."}
                  </div>
                ) : (
                  // It CAN still be cancelled — the room is released — there's
                  // just no money back. The old copy said it couldn't be, which
                  // contradicted the Cancel button right above it.
                  <div className="text-sm text-ink-soft">This rate didn't include a refund. You can still cancel to release the room, but the {usd(payToday, currency)} you paid won't be returned.</div>
                )}
              </div>
            )}

            <div className="card p-5 print:hidden">
              <h2 className="font-display text-base text-ink mb-2">What's next?</h2>
              <ul className="prose-body text-sm space-y-1.5 list-disc pl-5">
                <li>Bring a government-issued ID matching the lead guest name.</li>
                <li>Check-in and check-out times are set by the property — see the confirmation email.</li>
                <li>Local taxes and resort fees listed on the right are paid at the property.</li>
              </ul>
            </div>

            <a href="/" className="btn-primary text-sm print:hidden">Back to Aloha Rates</a>
          </div>

          {/* ────────── Right: Hotel + itemized total ────────── */}
          <aside className="lg:sticky lg:top-24 space-y-4 print:break-inside-avoid">
            <div className="card overflow-hidden print:shadow-none print:border print:border-ink/10">
              {heroPhoto && (
                <div className="aspect-[4/3] bg-sand-100">
                  <img src={heroPhoto} alt={hotelName} onError={e => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover"/>
                </div>
              )}
              <div className="p-5 space-y-3">
                <div>
                  <div className="font-display text-base text-ink leading-snug">{hotelName}</div>
                  {roomName && <div className="text-xs text-ink-muted mt-0.5">{roomName}</div>}
                  {boardName && boardName !== "Room Only" && <div className="text-[11px] text-ocean-700 mt-1">{boardName}</div>}
                </div>
                <div className="border-t border-ink/5 pt-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-ink-soft"><CalendarIcon size={14} className="text-ocean-600"/>
                    <span>{ci && fmtLong(ci)} → {co && fmtLong(co)}</span>
                  </div>
                  <div className="flex items-start gap-2 text-ink-soft"><UserIcon size={14} className="text-ocean-600 mt-0.5"/>
                    <span>
                      {adults} adult{adults !== 1 ? "s" : ""}{childrenCount ? ` · ${childrenCount} child${childrenCount !== 1 ? "ren" : ""}` : ""}
                      {childrenAges.length > 0 && <span className="block text-[11px] text-ink-muted">Ages {childrenAges.join(", ")}</span>}
                    </span>
                  </div>
                </div>

                {/* Price breakdown */}
                <div className="border-t border-ink/5 pt-3 text-sm space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-ink-soft">
                      <span>Room subtotal</span>
                      <span>{usd(roomSubtotal, currency)}</span>
                    </div>
                    {taxesIncluded.map((t, i) => (
                      <div key={"i"+i} className="flex justify-between text-ink-muted text-xs">
                        <span>{t.description}</span>
                        <span>{usd(t.amount, currency)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-ink/5 text-ink font-semibold">
                      <span>Paid today</span>
                      <span>{usd(payToday, currency)}</span>
                    </div>
                  </div>

                  {taxesExcluded.length > 0 && (
                    <div className="space-y-1.5 rounded-2xl bg-sand-100/70 p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Pay at property</div>
                      {taxesExcluded.map((t, i) => (
                        <div key={"e"+i} className="flex justify-between text-ink-soft">
                          <span>{t.description}</span>
                          <span>{usd(t.amount, currency)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t border-ink/10 text-ink font-semibold">
                        <span>Collected at check-in</span>
                        <span>{usd(payAtProperty, currency)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between pt-2 border-t border-ink/10 text-ink font-semibold text-base">
                    <span>Trip total</span>
                    <span>{usd(tripTotal, currency)}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Footer for the printed page */}
        <div className="mt-6 hidden print:block text-[10px] text-ink-muted text-center border-t border-ink/10 pt-3">
          Booking receipt · Aloha Rates · aloharates.com · Reference {booking.bookingId}
        </div>
      </div>
    </div>
  );
}
